/**
 * Copyright 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import queryString from "query-string";
import Popup from "react-popup";
import {
  ApiKey,
  CustomerAccess,
  useGetAccessibleCustomersQuery,
  useGetMyApiKeyQuery,
  useSelfGrantCustomerRoleToApiKeyMutation,
  useSelfRemoveApiKeyMutation,
  useSelfRevokeCustomerRoleFromApiKeyMutation,
} from "../../../generated/graphql";
import CustomersTable from "../../../shared/customers/CustomersTable";
import ApiKeyBasicInformation from "../../../shared/apiKeys/ApiKeyBasicInformation";
import ApiKeyDetailsWrapper from "../../apiKeys/components/apiKeyDetails/ApiKeyDetailsWrapper";
import Button from "../../../commonComponents/button/Button";
import { unstable_batchedUpdates } from "react-dom";
import CustomersGrantRoleModal from "../../../shared/customers/CustomersGrantRoleModal";
import CustomersUpdateRoleModal from "../../../shared/customers/CustomersUpdateRoleModal";
import { ROUTES } from "../../../routes/routes";

const MyApiKeyDetails: React.FC = () => {
  const [myApiKeyData, setMyApiKeyData] = useState<ApiKey>();
  const [grantModalIsOpen, setGrantModalIsOpen] = useState<boolean>(false);
  const [updateModalIsOpen, setUpdateModalIsOpen] = useState<boolean>(false);
  const [customerData, setCustomerData] = useState<CustomerAccess>();

  const history = useHistory();
  const location = useLocation();
  // Filter
  const filtersFromUrl = () => {
    if (location.search === "") return "";

    const params = queryString.parse(location.search);
    return JSON.parse(params.filters as string);
  };
  const [filters, setFilters] = useState<string>(filtersFromUrl);

  // Page
  const pageFromUrl = (): number => {
    if (location.search === "") return 1;

    const params = queryString.parse(location.search);
    const pageUrl = params.page;
    if (pageUrl) {
      return +pageUrl;
    }

    return 1;
  };

  const [currentPage, setCurrentPage] = useState<number>(pageFromUrl);

  const { id: apiKeyId = "" } = useParams();

  useEffect(() => {
    unstable_batchedUpdates(() => {
      setFilters(filtersFromUrl());
      setCurrentPage(pageFromUrl());
    });
  }, [location]);

  const { data, loading, refetch, error } = useGetMyApiKeyQuery({
    variables: {
      id: apiKeyId,
      customerSearchCriteria: {
        pageNumber: currentPage,
        ...(filters && { customerId: filters }),
      },
    },
  });

  const [removeApiKey] = useSelfRemoveApiKeyMutation({
    variables: {
      id: apiKeyId,
    },
  });

  const { myApiKey } = data || {};

  useEffect(() => {
    setMyApiKeyData(myApiKey);
  }, [myApiKey]);

  const { data: customersList, loading: customersLoading } =
    useGetAccessibleCustomersQuery();

  return (
    <ApiKeyDetailsWrapper
      loading={loading}
      error={error}
      apiKey={myApiKeyData}
      redirect={ROUTES.myApiKeys.index.generate()}
    >
      {myApiKeyData && (
        <>
          <ApiKeyBasicInformation
            apiKeyData={myApiKeyData}
            id={apiKeyId}
            redirect={ROUTES.myApiKeys.index.generate()}
            removeApiKey={removeApiKey}
          />
          <div className="box is-clearfix">
            <CustomersTable
              history={history}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              setIsModalOpen={setUpdateModalIsOpen}
              isModalOpen={updateModalIsOpen}
              setFilters={setFilters}
              filters={filters}
              loading={loading}
              setCustomerData={setCustomerData}
              customersData={myApiKeyData.customerAccesses}
              customersList={customersList}
              customersLoading={customersLoading}
              property="accessibleCustomers"
            />
            <Button onClick={() => setGrantModalIsOpen(true)}>
              Grant customers accesses
            </Button>
          </div>
          <Popup />
          {grantModalIsOpen && (
            <CustomersGrantRoleModal
              setIsOpen={setGrantModalIsOpen}
              customersData={myApiKeyData.customerAccesses.data}
              identifier={apiKeyId}
              mutation={useSelfGrantCustomerRoleToApiKeyMutation}
              type="id"
              customersList={customersList}
              customersLoading={customersLoading}
              property="accessibleCustomers"
              refetch={refetch}
            />
          )}
          {updateModalIsOpen && customerData && (
            <CustomersUpdateRoleModal
              setIsOpen={setUpdateModalIsOpen}
              customerData={customerData}
              identifier={apiKeyId}
              type="id"
              mutationGrant={useSelfGrantCustomerRoleToApiKeyMutation}
              mutationRevoke={useSelfRevokeCustomerRoleFromApiKeyMutation}
              refetch={refetch}
            />
          )}
        </>
      )}
    </ApiKeyDetailsWrapper>
  );
};

export default MyApiKeyDetails;
